import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Cookie, X, Settings2, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const Toggle = ({ checked, onChange, disabled = false }: { checked: boolean; onChange?: () => void; disabled?: boolean }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`
      relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent 
      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#a5694f] focus:ring-offset-2
      ${checked ? (disabled ? 'bg-[#a5694f]/50' : 'bg-[#a5694f]') : 'bg-stone-200'}
      ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
    `}
  >
    <motion.span
      layout
      className={`
        inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
      `}
      animate={{ x: checked ? 20 : 0 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
    />
  </button>
);

const CookieBanner: React.FC = () => {
  const { i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const [cookieSettings, setCookieSettings] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const lang = (i18n.language?.split("-")[0] || "en").toLowerCase();
  const isES = lang === "es";
  const isFR = lang === "fr";

  useEffect(() => {
    const consent = localStorage.getItem('dem_cookie_consent');
    const savedSettings = localStorage.getItem('dem_cookie_settings');

    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setCookieSettings({
          necessary: true,
          analytics: Boolean(parsed?.analytics),
          marketing: Boolean(parsed?.marketing),
        });
      } catch (error) {
        console.error("Failed to parse cookie settings", error);
      }
    }

    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const handleOpenSettings = () => {
      const savedSettings = localStorage.getItem('dem_cookie_settings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          setCookieSettings({
            necessary: true,
            analytics: Boolean(parsed?.analytics),
            marketing: Boolean(parsed?.marketing),
          });
        } catch (error) {
          console.error("Failed to parse cookie settings", error);
        }
      }
      setIsVisible(true);
      setShowSettings(true);
    };
    window.addEventListener('open-cookie-settings', handleOpenSettings);
    return () => window.removeEventListener('open-cookie-settings', handleOpenSettings);
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('dem_cookie_consent', 'all');
    const nextSettings = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem('dem_cookie_settings', JSON.stringify(nextSettings));
    setCookieSettings(nextSettings);
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleDeclineAll = () => {
    localStorage.setItem('dem_cookie_consent', 'necessary_only');
    const nextSettings = { necessary: true, analytics: false, marketing: false };
    localStorage.setItem('dem_cookie_settings', JSON.stringify(nextSettings));
    setCookieSettings(nextSettings);
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('dem_cookie_consent', 'custom');
    localStorage.setItem('dem_cookie_settings', JSON.stringify(cookieSettings));
    setIsVisible(false);
    setShowSettings(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0, transition: { duration: 0.2 } }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-stone-900/40 backdrop-blur-sm"
        >
          <div className="bg-white rounded-3xl shadow-[0_40px_80px_-15px_rgba(0,0,0,0.5)] border border-stone-100 overflow-hidden w-full max-w-2xl max-h-[90vh] flex flex-col">
            {showSettings ? (
              <div className="p-8 md:p-10 flex flex-col gap-8">
                <div className="flex items-center justify-between border-b border-stone-100 pb-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-stone-50 border border-stone-100 flex items-center justify-center text-[#a5694f]">
                      <Settings2 size={20} />
                    </div>
                    <div>
                      <h3 className="font-oswald uppercase tracking-widest text-stone-900 text-lg">
                        {isES ? "Configuración" : isFR ? "Paramètres" : "Settings"}
                      </h3>
                      <p className="font-sans text-xs text-stone-400 mt-1">
                        {isES ? "Personalice sus preferencias" : isFR ? "Personnalisez vos préférences" : "Customize your preferences"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="w-10 h-10 rounded-full flex items-center justify-center bg-stone-50 text-stone-400 hover:text-stone-900 hover:bg-stone-100 transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="space-y-6 max-h-[50vh] overflow-y-auto pr-4 scrollbar-hide">
                  {/* Necessary */}
                  <div className="flex items-start justify-between gap-6 p-4 rounded-2xl bg-stone-50/50 border border-stone-100">
                    <div className="flex-1">
                      <h4 className="font-playfair font-bold text-stone-900 text-base mb-2">
                        {isES ? "Estrictamente Necesarias" : isFR ? "Strictement Nécessaires" : "Strictly Necessary"}
                      </h4>
                      <p className="font-sans font-light text-[13px] text-stone-500 leading-relaxed">
                        {isES ? "Requeridas para que el sitio funcione y no se pueden desactivar." : isFR ? "Requises pour le fonctionnement du site, ne peuvent être désactivées." : "Required for the website to function and cannot be switched off."}
                      </p>
                    </div>
                    <div className="pt-1 flex-shrink-0">
                      <Toggle checked={true} disabled={true} />
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between gap-6 p-4 rounded-2xl border border-stone-100 transition-colors hover:bg-stone-50/30">
                    <div className="flex-1">
                      <h4 className="font-playfair font-bold text-stone-900 text-base mb-2">
                        {isES ? "Analíticas" : isFR ? "Analytiques" : "Analytics"}
                      </h4>
                      <p className="font-sans font-light text-[13px] text-stone-500 leading-relaxed">
                        {isES ? "Nos ayudan a entender cómo interactúa con el sitio, qué páginas visita y cómo mejorar la experiencia." : isFR ? "Nous aident à comprendre comment vous interagissez avec le site, quelles pages vous visitez et comment nous pouvons nous améliorer." : "Help us understand how you interact with the website, which pages you visit and how we can improve."}
                      </p>
                    </div>
                    <div className="pt-1 flex-shrink-0">
                      <Toggle checked={cookieSettings.analytics} onChange={() => setCookieSettings(prev => ({...prev, analytics: !prev.analytics}))} />
                    </div>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-start justify-between gap-6 p-4 rounded-2xl border border-stone-100 transition-colors hover:bg-stone-50/30">
                    <div className="flex-1">
                      <h4 className="font-playfair font-bold text-stone-900 text-base mb-2">
                        Marketing
                      </h4>
                      <p className="font-sans font-light text-[13px] text-stone-500 leading-relaxed">
                        {isES ? "Utilizadas para ofrecerle publicidad relevante y contenido personalizado." : isFR ? "Utilisés pour vous proposer des publicités pertinentes et du contenu personnalisé." : "Used to deliver relevant advertising to you and personalized content."}
                      </p>
                    </div>
                    <div className="pt-1 flex-shrink-0">
                      <Toggle checked={cookieSettings.marketing} onChange={() => setCookieSettings(prev => ({...prev, marketing: !prev.marketing}))} />
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-stone-100 flex justify-end">
                  <button
                    onClick={handleSaveSettings}
                    className="w-full sm:w-auto bg-stone-900 text-white font-oswald uppercase tracking-widest text-[11px] md:text-xs py-4 px-8 rounded-xl hover:bg-[#a5694f] transition-colors flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
                  >
                    <Check size={16} />
                    {isES ? "Guardar y Cerrar" : isFR ? "Enregistrer et Fermer" : "Save and Close"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-8 md:p-10 flex flex-col gap-8 relative">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 rounded-full bg-[#a5694f]/10 flex items-center justify-center text-[#a5694f]">
                    <Cookie size={28} />
                  </div>
                  <div>
                    <h3 className="font-oswald uppercase tracking-[0.2em] text-stone-900 text-xl">
                      {isES ? "Privacidad y Cookies" : isFR ? "Confidentialité et Cookies" : "Privacy & Cookies"}
                    </h3>
                    <p className="font-sans text-xs text-stone-400 mt-1">
                      {isES ? "Su experiencia nos importa" : isFR ? "Votre expérience nous importe" : "Your experience matters"}
                    </p>
                  </div>
                </div>
                
                <p className="font-sans font-light text-stone-600 text-sm md:text-[15px] leading-relaxed">
                  {isES 
                    ? "Utilizamos cookies propias y de terceros para mejorar su experiencia, analizar nuestro tráfico y personalizar el contenido. Puede aceptar todas las cookies, rechazarlas o configurar sus preferencias." 
                    : isFR 
                    ? "Nous utilisons des cookies propres et de tiers pour améliorer votre expérience, analyser notre trafic et personnaliser le contenu. Vous pouvez accepter tous les cookies, les refuser ou configurer vos préférences." 
                    : "We use our own and third-party cookies to improve your experience, analyze our traffic, and personalize content. You can accept all cookies, decline them, or configure your preferences."
                  }
                  <br />
                  <Link to="/privacy-policy" className="inline-block mt-2 font-medium underline underline-offset-4 text-[#a5694f] hover:text-stone-900 transition-colors">
                    {isES ? "Leer Política de Privacidad" : isFR ? "Lire la Politique de confidentialité" : "Read Privacy Policy"}
                  </Link>
                </p>

                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-stone-100">
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-stone-900 text-white font-oswald uppercase tracking-widest text-[11px] md:text-xs py-4 px-6 rounded-3xl hover:bg-[#a5694f] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  >
                    {isES ? "Aceptar todas" : isFR ? "Tout accepter" : "Accept All"}
                  </button>
                  <button
                    onClick={handleDeclineAll}
                    className="flex-1 bg-stone-50 border border-stone-200 text-stone-700 font-oswald uppercase tracking-widest text-[11px] md:text-xs py-4 px-6 rounded-3xl hover:bg-stone-100 hover:text-stone-900 transition-all hover:-translate-y-0.5"
                  >
                    {isES ? "Solo necesarias" : isFR ? "Essentiels" : "Necessary Only"}
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="flex-1 bg-white border border-stone-200 text-stone-600 hover:text-stone-900 hover:border-stone-300 font-oswald uppercase tracking-widest text-[11px] md:text-xs py-4 px-6 rounded-3xl transition-all flex items-center justify-center gap-2 hover:-translate-y-0.5"
                  >
                    <Settings2 size={16} />
                    <span>
                      {isES ? "Ajustes" : isFR ? "Gérer" : "Settings"}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;